<template>
  <HrPageLayout
    title="Position & Roles Management"
    subtitle="Assign roles to job positions for access control"
    icon="🏢"
    :breadcrumb-items="[
      { label: 'System Administration' },
      { label: 'Position & Roles' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-secondary" :disabled="loading" @click="openCreatePosition()">
        <i class="bx bx-plus"></i> Create Position
      </button>
      <button type="button" class="hr-btn hr-btn-primary" :disabled="loading" @click="loadData()">
        <i class="bx bx-refresh"></i> {{ loading ? 'Loading...' : 'Refresh' }}
      </button>
    </template>

    <div>
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-briefcase"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Positions</p>
          <h2 class="hr-metric-value">{{ totalPositions }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-shield-account"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Roles</p>
          <h2 class="hr-metric-value">{{ totalRoles }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-link"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Assignments</p>
          <h2 class="hr-metric-value">{{ totalAssignments }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-percent"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Coverage</p>
          <h2 class="hr-metric-value">{{ positionsWithRoles }}%</h2>
            </div>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-header hr-flex-between">
          <div class="prm-search-wrap">
            <i class="bx bx-search-alt search-icon"></i>
            <input v-model="positionSearch" type="text" class="hr-input" placeholder="Search positions or roles...">
            <button v-if="positionSearch" type="button" class="prm-clear" @click="positionSearch = ''"><i class="bx bx-x"></i></button>
          </div>
          <p class="hr-text-muted hr-mb-0">{{ filteredPositions.length }} position(s)</p>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Loading positions...</p>
          </div>
          <div v-else-if="filteredPositions.length === 0" class="hr-empty-state">
            <i class="bx bx-briefcase"></i>
            <p>No positions match your search.</p>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Position</th>
                  <th>Assigned roles</th>
                  <th class="hr-text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="position in filteredPositions" :key="position.id">
                  <td>
                    <strong>{{ position.positionName }}</strong>
                    <span v-if="position.code" class="hr-badge hr-badge-secondary hr-ml-1">{{ position.code }}</span>
                    <p v-if="position.description" class="hr-text-muted hr-small hr-mb-0">{{ position.description }}</p>
                  </td>
                  <td>
                    <div class="role-badges">
                      <template v-if="position.roleNames && position.roleNames.length">
                        <span v-for="role in position.roleNames.slice(0, 6)" :key="role" class="hr-badge hr-badge-primary">{{ role }}</span>
                        <span v-if="position.roleNames.length > 6" class="hr-badge hr-badge-secondary">+{{ position.roleNames.length - 6 }} more</span>
                      </template>
                      <span v-else class="hr-text-muted">No roles assigned</span>
                    </div>
                  </td>
                  <td class="hr-text-end prm-actions-cell">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" title="Edit details" @click="openEditPosition(position)">
                      <i class="bx bx-edit"></i>
                    </button>
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="editPosition(position)">
                      <i class="bx bx-shield"></i> Roles
                    </button>
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-danger" @click="confirmDeletePosition(position)">
                      <i class="bx bx-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit position details -->
    <div v-if="showEditPositionModal" class="hr-modal-overlay" @click.self="closeEditPositionModal()">
      <div class="hr-modal-dialog">
        <div class="hr-modal-content">
          <div class="hr-modal-header">
            <h5 class="hr-modal-title">Edit Position</h5>
            <button type="button" class="hr-btn-close" @click="closeEditPositionModal()"></button>
          </div>
          <div class="hr-modal-body">
            <div class="hr-form-grid">
              <div class="hr-filter-group span-full">
                <label class="hr-form-label">Position Name *</label>
                <input v-model="editPositionForm.positionName" type="text" class="hr-input">
              </div>
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Code</label>
                <input v-model="editPositionForm.code" type="text" class="hr-input">
              </div>
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Level</label>
                <input v-model.number="editPositionForm.level" type="number" min="0" class="hr-input">
              </div>
              <div class="hr-filter-group span-full">
                <label class="hr-form-label">Description</label>
                <textarea v-model="editPositionForm.description" class="hr-textarea" rows="2"></textarea>
              </div>
            </div>
          </div>
          <div class="hr-modal-footer">
            <button type="button" class="hr-btn hr-btn-secondary" @click="closeEditPositionModal()">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="savingEditPosition" @click="saveEditPosition()">Save</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit roles modal (grouped) -->
    <div v-if="selectedPosition" class="hr-modal-overlay" @click.self="selectedPosition = null">
      <div class="hr-modal-dialog modal-lg">
        <div class="hr-modal-content">
          <div class="hr-modal-header">
            <h5 class="hr-modal-title">Roles for: <strong>{{ selectedPosition.positionName }}</strong></h5>
            <button type="button" class="hr-btn-close" @click="selectedPosition = null"></button>
          </div>
          <div class="hr-modal-body">
            <div class="prm-role-toolbar hr-mb-3">
              <div class="prm-search-wrap">
                <i class="bx bx-search-alt search-icon"></i>
                <input v-model="roleSearch" type="text" class="hr-input" placeholder="Search roles...">
              </div>
              <div class="d-flex gap-2 flex-wrap">
                <button type="button" class="hr-btn hr-btn-sm hr-btn-outline" @click="assignAllRolesInEdit()">Select all</button>
                <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click="selectedRoles = []">Clear</button>
                <span class="hr-text-muted align-self-center">{{ selectedRoles.length }} / {{ allRoles.length }}</span>
              </div>
            </div>
            <div v-for="(roles, groupName) in filteredGroupedRoles" :key="groupName" class="prm-role-group hr-mb-2">
              <div class="prm-role-group-head" @click="toggleRoleGroup(groupName)">
                <span><i class="bx bx-folder"></i> {{ groupName }} <span class="hr-badge hr-badge-secondary">{{ roles.length }}</span></span>
                <button type="button" class="hr-btn hr-btn-sm hr-btn-link" @click.stop="toggleGroupSelection(roles)">
                  {{ isGroupFullySelected(roles) ? 'Deselect' : 'Select all' }}
                </button>
                <i :class="expandedRoleGroups.includes(groupName) ? 'bx bx-chevron-up' : 'bx bx-chevron-down'"></i>
              </div>
              <div v-show="expandedRoleGroups.includes(groupName)" class="prm-role-group-body">
                <label v-for="role in roles" :key="role.id" class="prm-role-check">
                  <input v-model="selectedRoles" :value="role.id" type="checkbox">
                  <span>{{ role.role_name }}</span>
                  <small v-if="role.description" class="hr-text-muted">{{ role.description }}</small>
                </label>
              </div>
            </div>
            <div v-if="Object.keys(filteredGroupedRoles).length === 0" class="hr-empty-state-small">No roles match search.</div>
          </div>
          <div class="hr-modal-footer">
            <button type="button" class="hr-btn hr-btn-secondary" @click="selectedPosition = null">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="savingRoles" @click="savePositionRoles()">Save Roles</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create position modal -->
    <div v-if="showCreateModal" class="hr-modal-overlay" @click.self="closeCreateModal()">
      <div class="hr-modal-dialog modal-lg">
        <div class="hr-modal-content">
          <div class="hr-modal-header">
            <h5 class="hr-modal-title">Create Position</h5>
            <button type="button" class="hr-btn-close" @click="closeCreateModal()"></button>
          </div>
          <div class="hr-modal-body">
            <div class="hr-form-grid hr-mb-4">
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Position Name *</label>
                <input v-model="createForm.positionName" type="text" class="hr-input">
              </div>
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Code</label>
                <input v-model="createForm.code" type="text" class="hr-input" placeholder="Optional — auto-generated if blank">
              </div>
              <div class="hr-filter-group span-full">
                <label class="hr-form-label">Description</label>
                <textarea v-model="createForm.description" class="hr-textarea" rows="2"></textarea>
              </div>
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Level</label>
                <input v-model.number="createForm.level" type="number" min="0" class="hr-input">
              </div>
            </div>
            <label class="hr-form-label"><strong>Assign roles</strong></label>
            <div class="d-flex gap-2 hr-mb-2">
              <button type="button" class="hr-btn hr-btn-sm hr-btn-outline" @click="assignAllRolesInCreate()">Select all</button>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click="createForm.roleIds = []">Clear</button>
            </div>
            <div class="role-selection-list">
              <label v-for="role in allRoles" :key="'c-' + role.id" class="prm-role-check">
                <input v-model="createForm.roleIds" :value="role.id" type="checkbox">
                <span>{{ role.role_name }}</span>
              </label>
            </div>
          </div>
          <div class="hr-modal-footer">
            <button type="button" class="hr-btn hr-btn-secondary" @click="closeCreateModal()">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="savingPosition" @click="saveNewPosition()">Create</button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import { authUrl, execute, toast, refreshSessionRoles } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
export default {
  name: "PositionRoleManagement",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      savingRoles: false,
      savingPosition: false,
      positions: [],
      allRoles: [],
      selectedPosition: null,
      selectedRoles: [],
      totalPositions: 0,
      totalRoles: 0,
      totalAssignments: 0,
      positionSearch: "",
      roleSearch: "",
      expandedRoleGroups: [],
      showCreateModal: false,
      createForm: { positionName: "", code: "", description: "", level: 0, roleIds: [] },
      showEditPositionModal: false,
      savingEditPosition: false,
      editPositionForm: { positionId: null, positionName: "", code: "", description: "", level: 0 }
    };
  },
  computed: {
    positionsWithRoles() {
      if (this.totalPositions === 0) return 0;
      const withRoles = this.positions.filter((p) => (p.roleNames || []).length > 0).length;
      return Math.round((withRoles / this.totalPositions) * 100);
    },
    filteredPositions() {
      const q = (this.positionSearch || "").toLowerCase().trim();
      if (!q) return this.positions;
      return this.positions.filter((p) => {
        const roles = (p.roleNames || []).join(" ").toLowerCase();
        return (
          (p.positionName || "").toLowerCase().includes(q) ||
          (p.code || "").toLowerCase().includes(q) ||
          (p.description || "").toLowerCase().includes(q) ||
          roles.includes(q)
        );
      });
    },
    groupedRoles() {
      const grouped = {};
      this.allRoles.forEach((role) => {
        const cat = role.category || "Other";
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(role);
      });
      return grouped;
    },
    filteredGroupedRoles() {
      const q = (this.roleSearch || "").toLowerCase().trim();
      if (!q) return this.groupedRoles;
      const filtered = {};
      Object.keys(this.groupedRoles).forEach((groupName) => {
        const matching = this.groupedRoles[groupName].filter(
          (r) =>
            r.role_name.toLowerCase().includes(q) ||
            groupName.toLowerCase().includes(q) ||
            (r.description && r.description.toLowerCase().includes(q))
        );
        if (matching.length) filtered[groupName] = matching;
      });
      return filtered;
    }
  },
  mounted() {
    sessionStorage.setItem("selected", "position-role-management");
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const posData = new FormData();
        posData.append("function", "getPositionsWithRoles");
        const posRes = await execute(posData, authUrl);
        if (posRes?.data?.success) {
          this.positions = posRes.data.data.positions || [];
          this.totalPositions = posRes.data.data.total || 0;
          this.totalAssignments = this.positions.reduce((sum, p) => sum + (p.roleCount || 0), 0);
        }
        const roleData = new FormData();
        roleData.append("function", "getAllRoles");
        const roleRes = await execute(roleData, authUrl);
        if (roleRes?.data?.success) {
          this.allRoles = (roleRes.data.data.roles || []).filter((r) => r.status === "active");
          this.totalRoles = this.allRoles.length;
          this.expandedRoleGroups = Object.keys(this.groupedRoles);
        }
      } catch (e) {
        toast.error("Failed to load: " + (e.message || "error"));
      } finally {
        this.loading = false;
      }
    },
    editPosition(position) {
      this.selectedPosition = position;
      this.roleSearch = "";
      this.selectedRoles = this.allRoles
        .filter((r) => (position.roleNames || []).includes(r.role_name))
        .map((r) => r.id);
      this.expandedRoleGroups = Object.keys(this.groupedRoles);
    },
    toggleRoleGroup(name) {
      const i = this.expandedRoleGroups.indexOf(name);
      if (i > -1) this.expandedRoleGroups.splice(i, 1);
      else this.expandedRoleGroups.push(name);
    },
    isGroupFullySelected(roles) {
      return roles.every((r) => this.selectedRoles.includes(r.id));
    },
    toggleGroupSelection(roles) {
      const all = this.isGroupFullySelected(roles);
      roles.forEach((r) => {
        const idx = this.selectedRoles.indexOf(r.id);
        if (all && idx > -1) this.selectedRoles.splice(idx, 1);
        else if (!all && idx === -1) this.selectedRoles.push(r.id);
      });
    },
    openEditPosition(position) {
      this.editPositionForm = {
        positionId: position.id,
        positionName: position.positionName || "",
        code: position.code || "",
        description: position.description || "",
        level: position.level != null ? position.level : 0
      };
      this.showEditPositionModal = true;
    },
    closeEditPositionModal() {
      this.showEditPositionModal = false;
    },
    async saveEditPosition() {
      if (!this.editPositionForm.positionId || !this.editPositionForm.positionName.trim()) return;
      this.savingEditPosition = true;
      try {
        const data = new FormData();
        data.append("function", "updatePosition");
        data.append("positionId", this.editPositionForm.positionId);
        data.append("positionName", this.editPositionForm.positionName.trim());
        data.append("code", this.editPositionForm.code);
        data.append("description", this.editPositionForm.description);
        data.append("level", String(this.editPositionForm.level || 0));
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          toast.success("Position updated");
          this.closeEditPositionModal();
          this.loadData();
        } else toast.error(res?.data?.message || "Update failed");
      } finally {
        this.savingEditPosition = false;
      }
    },
    confirmDeletePosition(position) {
      if (!confirm(`Delete position "${position.positionName}"?`)) return;
      this.deletePosition(position);
    },
    async deletePosition(position) {
      const data = new FormData();
      data.append("function", "deletePosition");
      data.append("positionId", position.id);
      const res = await execute(data, authUrl);
      if (res?.data?.success) {
        toast.success("Position deleted");
        this.loadData();
      } else toast.error(res?.data?.message || "Delete failed");
    },
    openCreatePosition() {
      this.createForm = { positionName: "", code: "", description: "", level: 0, roleIds: [] };
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },
    assignAllRolesInCreate() {
      this.createForm.roleIds = this.allRoles.map((r) => r.id);
    },
    assignAllRolesInEdit() {
      this.selectedRoles = this.allRoles.map((r) => r.id);
    },
    async saveNewPosition() {
      const name = (this.createForm.positionName || "").trim();
      if (!name) {
        toast.warning("Position name is required");
        return;
      }
      this.savingPosition = true;
      try {
        const data = new FormData();
        data.append("function", "createPosition");
        data.append("positionName", name);
        data.append("code", this.createForm.code);
        data.append("description", this.createForm.description);
        data.append("level", String(this.createForm.level || 0));
        data.append("roleIds", JSON.stringify(this.createForm.roleIds || []));
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          const createdCode = res.data.data?.code;
          toast.success(createdCode ? `Position created (${createdCode})` : "Position created");
          this.closeCreateModal();
          this.loadData();
        } else toast.error(res?.data?.message || "Failed to create position");
      } finally {
        this.savingPosition = false;
      }
    },
    async savePositionRoles() {
      if (!this.selectedPosition) return;
      this.savingRoles = true;
      try {
        const data = new FormData();
        data.append("function", "updatePositionRoles");
        data.append("positionId", this.selectedPosition.id);
        data.append("roleIds", JSON.stringify(this.selectedRoles));
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          try {
            await refreshSessionRoles();
            toast.success("Roles updated — sidebar menu refreshed");
          } catch {
            toast.success("Roles saved. Log out and back in to refresh your menu.");
          }
          this.selectedPosition = null;
          this.loadData();
        } else toast.error(res?.data?.message || "Failed");
      } finally {
        this.savingRoles = false;
      }
    }
  }
};
</script>

<style scoped>
.prm-search-wrap {
  position: relative;
  flex: 1;
  min-width: 220px;
  max-width: 400px;
}
.prm-search-wrap .hr-input {
  padding-left: 38px;
  padding-right: 36px;
}
.prm-search-wrap .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}
.prm-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  color: #64748b;
  cursor: pointer;
}
.hr-flex-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.role-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.prm-actions-cell {
  white-space: nowrap;
}
.prm-actions-cell .hr-btn {
  margin-left: 4px;
}
.hr-loading-wrap {
  text-align: center;
  padding: 3rem;
}
.hr-empty-state {
  text-align: center;
  padding: 2.5rem;
  color: #94a3b8;
}
.hr-empty-state i {
  font-size: 48px;
  display: block;
  margin-bottom: 12px;
}
.hr-ml-1 {
  margin-left: 6px;
}
.prm-role-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}
.prm-role-group {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}
.prm-role-group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8fafc;
  cursor: pointer;
  font-weight: 600;
  gap: 8px;
}
.prm-role-group-body {
  padding: 8px 14px 12px;
  max-height: 200px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 6px;
}
.prm-role-check {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
}
.prm-role-check:hover {
  background: #f1f5f9;
}
.prm-role-check input {
  margin-right: 6px;
}
.role-selection-list {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px;
}
.hr-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.hr-modal-dialog {
  background: #fff;
  border-radius: 10px;
  width: 92%;
  max-width: 520px;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}
.modal-lg {
  max-width: 720px !important;
}
.hr-modal-header,
.hr-modal-footer {
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}
.hr-modal-footer {
  border-bottom: none;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.hr-modal-body {
  padding: 20px;
}
.hr-modal-title {
  margin: 0;
  font-size: 1.1rem;
}
.hr-btn-close {
  border: none;
  background: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #94a3b8;
}
.hr-empty-state-small {
  padding: 16px;
  text-align: center;
  color: #94a3b8;
}
</style>
